import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, Check, Compass } from 'lucide-react';
import {
  DIFFICULTY_AXIS_LABELS,
  LearningMode,
  OnboardingAnswers,
  computeOnboardingSettings,
  getOnboardingQuestions
} from '@unity-learning/shared';
import { useUser } from '../hooks/useUser';
import { useLearningMode } from '../contexts/LearningModeContext';
import { completeOnboarding, isApiError } from '../services/api';
import { trackEvent } from '../utils/analytics';

/**
 * Questionnaire de niveau du premier login : 4-5 questions à choix, puis un aperçu des
 * réglages de difficulté déduits (même calcul que le serveur, `computeOnboardingSettings`
 * de shared). Affiché par HomePage tant que `user.onboardingCompletedAt` est null ; « Passer »
 * pose la date sans toucher aux réglages. Modifiable ensuite dans « Paramètres des Challenges ».
 * Rendu dans document.body (portal) : l'accueil contient des cartes `glass` à backdrop-filter,
 * qui feraient d'un `fixed inset-0` un enfant de leur boîte (voir AGENTS.md, piège 7).
 */
const OnboardingQuestionnaire: React.FC = () => {
  const { user, refreshUser } = useUser();
  const { learningMode, isUnityMode } = useLearningMode();
  const questions = useMemo(() => getOnboardingQuestions(learningMode), [learningMode]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Partial<OnboardingAnswers>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  const isSummary = step === questions.length;
  const current = questions[step];
  const currentValue = current ? answers[current.id] : undefined;
  const complete = questions.every(q => answers[q.id] !== undefined);
  const preview = complete ? computeOnboardingSettings(answers as OnboardingAnswers, learningMode) : null;

  const finish = async (skipped: boolean) => {
    setIsSaving(true);
    setError(null);
    try {
      await completeOnboarding(user.id, skipped
        ? { learningMode, skipped: true }
        : { learningMode, answers: answers as OnboardingAnswers });
      trackEvent('onboarding_completed', { learningMode, skipped, ...(skipped ? {} : answers) });
      refreshUser();
    } catch (err) {
      setError(isApiError(err) && !err.isNetworkError
        ? err.message || 'Impossible d\'enregistrer vos réponses'
        : 'Erreur réseau : vérifiez votre connexion puis réessayez.');
      setIsSaving(false);
    }
  };

  const goalHint = isUnityMode
    ? null
    : learningMode === LearningMode.GODOT
      ? 'La certification concerne Unity ; en Godot, choisissez « bases » ou « mon projet ».'
      : 'La certification concerne Unity ; en Python/JavaScript, choisissez « bases » ou « mon projet ».';

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="onboarding-title">
      <div className="w-full max-w-xl max-h-[92vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center shrink-0">
            <Compass className="w-5 h-5" />
          </div>
          <h2 id="onboarding-title" className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            Bienvenue, {user.pseudonym} ! Réglons vos exercices
          </h2>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
          {questions.length} questions, moins d'une minute : elles règlent la difficulté de vos premiers
          exercices. Vous pourrez tout changer plus tard dans « Paramètres des Challenges ».
        </p>

        {/* Barre d'avancement */}
        <div className="flex items-center gap-2 mb-6" aria-hidden="true">
          {questions.map((q, i) => (
            <div
              key={q.id}
              className={`h-1.5 flex-1 rounded-full ${i < step || isSummary ? 'bg-blue-600' : i === step ? 'bg-blue-400' : 'bg-gray-200 dark:bg-gray-700'}`}
            />
          ))}
        </div>

        {!isSummary && current && (
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              Question {step + 1} sur {questions.length}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{current.prompt}</h3>
            <div className="space-y-2" role="radiogroup" aria-label={current.prompt}>
              {current.options.map(option => {
                const selected = currentValue === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setAnswers(prev => ({ ...prev, [current.id]: option.value }))}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${selected
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-400'
                      : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500 bg-white dark:bg-gray-800/60'}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? 'border-blue-600 bg-blue-600 text-white dark:border-blue-400 dark:bg-blue-400' : 'border-gray-300 dark:border-gray-500'}`}>
                        {selected && <Check className="w-3 h-3" />}
                      </span>
                      <span>
                        <span className="block font-medium text-gray-900 dark:text-white">{option.label}</span>
                        {option.hint && <span className="block text-sm text-gray-500 dark:text-gray-400">{option.hint}</span>}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            {current.id === 'goal' && goalHint && (
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">{goalHint}</p>
            )}
          </div>
        )}

        {isSummary && preview && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Votre point de départ</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Vos premiers exercices seront réglés ainsi (c'est volontairement prudent : un exercice trop
              facile se corrige en deux clics, un exercice trop dur le premier jour décourage).
            </p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 p-3">
                <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Logique</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{DIFFICULTY_AXIS_LABELS.algorithmicComplexity[preview.algorithmicComplexity]}</dd>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 p-3">
                <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Étendue du code</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{DIFFICULTY_AXIS_LABELS.codeScope[preview.codeScope]}</dd>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 p-3">
                <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Notions par exercice</dt>
                <dd className="font-medium text-gray-900 dark:text-white">{DIFFICULTY_AXIS_LABELS.cognitiveLoad[preview.cognitiveLoad]}</dd>
              </div>
              {isUnityMode && preview.unityApiKnowledge !== undefined && (
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 p-3">
                  <dt className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">API Unity</dt>
                  <dd className="font-medium text-gray-900 dark:text-white">{DIFFICULTY_AXIS_LABELS.unityApiKnowledge[preview.unityApiKnowledge]}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {error && (
          <div className="mt-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded-lg text-sm" role="alert">
            {error}
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => finish(true)}
            disabled={isSaving}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline-offset-2 hover:underline disabled:opacity-50"
          >
            Passer pour l'instant
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep(s => s - 1)}
                disabled={isSaving}
                className="inline-flex items-center px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm font-medium disabled:opacity-50"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Précédent
              </button>
            )}
            {!isSummary ? (
              <button
                type="button"
                onClick={() => setStep(s => s + 1)}
                disabled={currentValue === undefined}
                className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {step === questions.length - 1 ? 'Voir mon point de départ' : 'Suivant'}
                <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => finish(false)}
                disabled={isSaving || !complete}
                className="inline-flex items-center px-5 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-semibold disabled:opacity-50"
              >
                {isSaving ? 'Enregistrement…' : 'C\'est parti !'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OnboardingQuestionnaire;
